import React, { Component, ReactNode, ErrorInfo } from 'react';

// Type definitions
interface ChunkLoadErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  retryCount: number;
  isChunkError: boolean;
}

interface ChunkLoadErrorBoundaryProps {
  children: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo, isChunkError: boolean) => void;
  errorMessage?: string;
}

/**
 * ChunkLoadErrorBoundary - Specialized error boundary for handling chunk loading failures
 * 
 * This component specifically handles errors that occur when lazy-loaded chunks fail to load,
 * which is common when there are network issues or when the app has been updated and old
 * chunks are no longer available.
 */
class ChunkLoadErrorBoundary extends Component<ChunkLoadErrorBoundaryProps, ChunkLoadErrorBoundaryState> {
  constructor(props: ChunkLoadErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      retryCount: 0,
      isChunkError: false,
};
}

  static getDerivedStateFromError(error: Error): Partial<ChunkLoadErrorBoundaryState> {
    // Check if this is a chunk loading error
    const isChunkError = 
      error?.name === 'ChunkLoadError' || 
      error?.message?.includes('Loading chunk') ||
      error?.message?.includes('Failed to import') ||
      error?.message?.includes('Loading CSS chunk');

    return {
      hasError: true,
      error: error,
      isChunkError,
};
}

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log chunk loading errors differently
    if (this.state.isChunkError) {
      console.warn('Chunk loading failed, likely due to network or cache issues:', error);
}

    // Call external error handler if provided
    if (this.props.onError && typeof this.props.onError === 'function') {
      this.props.onError(error, errorInfo, this.state.isChunkError);
}
}

  handleRetry = () => {
    if (this.state.isChunkError && this.state.retryCount < 3) {
      // For chunk errors, reload the page to get fresh chunks
      window.location.reload();
} else {
      // For other errors, just reset the boundary
      this.setState({
        hasError: false,
        error: null,
        retryCount: this.state.retryCount + 1,
  });
}
  };

  render() {
    if (this.state.hasError) {
      const { isChunkError, retryCount } = this.state;
      
      if (isChunkError) {
        return (
          <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
            <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 text-center">
              <div className="mb-4">
                <svg 
                  className="mx-auto h-12 w-12 text-yellow-400" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" 
                  />
                </svg>
              </div>
              
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Problema de Conexión
              </h2>
              
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                No se pudo cargar parte de la aplicación. Esto puede deberse a problemas de 
                conexión a internet o actualizaciones recientes.
              </p>
              
              <div className="space-y-3">
                <button
                  onClick={() => this.handleRetry()}
                  className="w-full px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                  aria-label="Recargar la página para resolver el problema"
                >
                  Recargar Página
                </button>
                
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  <p>Si el problema persiste:</p>
                  <ul className="mt-1 text-left space-y-1">
                    <li>• Verifica tu conexión a internet</li>
                    <li>• Limpia la caché del navegador</li>
                    <li>• Contacta soporte técnico</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );
}

      // For non-chunk errors, show retry option
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
          <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 text-center">
            <div className="mb-4">
              <svg 
                className="mx-auto h-12 w-12 text-red-400" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
                aria-hidden="true"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                />
              </svg>
            </div>
            
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Error Inesperado
            </h2>
            
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {this.props.errorMessage || 'Algo salió mal. Por favor, intenta nuevamente.'}
            </p>
            
            <div className="space-y-3">
              <button
                onClick={() => this.handleRetry()}
                disabled={retryCount >= 3}
                className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                aria-label="Intentar nuevamente"
              >
                {retryCount >= 3 ? 'Demasiados intentos' : 'Intentar Nuevamente'}
              </button>
              
              {retryCount >= 3 && (
                <button
                  onClick={() => window.location.reload()}
                  className="w-full px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  Recargar Página
                </button>
              )}
            </div>
          </div>
        </div>
      );
}

    return this.props.children;
}
}

export default ChunkLoadErrorBoundary;